import BaseComponentController from "../base_component_controller";
import TomSelect from "tom-select";
import { computePosition, flip, shift, offset } from "@floating-ui/dom";

export default class extends BaseComponentController {
  static values = {
    url: String, // URL to fetch options from
    valueField: { type: String, default: "value" }, // Field to use for value
    labelField: { type: String, default: "label" }, // Field to use for label
    disableDropdownInput: { type: Boolean, default: false }, // Disable dropdown input
    disableTyping: { type: Boolean, default: false }, // Disable typing
    allowNew: { type: Boolean, default: false }, // Allow new options
    updateField: { type: Boolean, default: false }, // Update field with selected value
    updateFieldTarget: { type: String }, // Target to update
    updateFieldSource: { type: String, default: "name" }, // Source to update
    perPage: { type: Number, default: 20 }, // Number of options per page
    virtualScroll: { type: Boolean, default: false }, // Use virtual scroll
    optgroupColumns: { type: Boolean, default: false }, // Use optgroup columns
    responseDataField: { type: String, default: "data" }, // Field in response containing array of items (auto-detects common patterns)
    searchParam: { type: String, default: "query" }, // Search parameter name for the API
    removeIcon: { type: String }, // Customize the remove button icon
    customOptions: { type: Object, default: {} }, // Add or override TomSelect Options (override all plugins for example)
    loadingMoreTemplate: { type: String }, // Customize the template for loading more case
    noMoreResultsTemplate: { type: String }, // Customize the template for no more results case
    noResultsTemplate: { type: String }, // Customize the template for no results case
    renderOptionTemplate: { type: String }, // Customize the template for render option case
    renderSelectedItemTemplate: { type: String }, // Customize the template for render selected item case
    optionCreateTemplate: { type: String }, // Customize the template for option create case
    clearButtonTemplate: { type: String }, // Customize the template for clear button case
    clearIcon: { type: String }, // Customize the clear button icon
    clearTitle: { type: String }, // Customize the clear button title
  };

  connect() {
    if (this.element.tomselect) return;

    const options = this.buildOptions();
    this.select = new TomSelect(this.element, options);

    this.setupEventHandlers();
    this.setupPositioning();
    this.handleInitialValue();

    this.element.style.visibility = "visible";
  }

  disconnect() {
    this.cleanup();
  }

  buildOptions() {
    const plugins = this.getPlugins();
    const baseOptions = {
      plugins,
      maxOptions: null,
      closeAfterSelect: !this.element.multiple,
      create: this.allowNewValue,
      render: this.getRenderConfig(),
      onChange: this.handleChange.bind(this),
      onDropdownOpen: () => this.updatePosition(),
    };

    if (!this.hasUrlValue) {
      return {
        ...baseOptions,
        ...this.customOptionsValue
      }
    }

    return {
      ...baseOptions,
      preload: true,
      ...(this.virtualScrollValue ? this.getVirtualScrollConfig() : this.getCustomScrollConfig()),
      ...this.customOptionsValue
    };
  }

  getPlugins() {
    const controller = this;
    const plugins = {};
    const isMultiple = this.element.multiple;
    const isDisabled = this.element.disabled;
    const useVirtualScroll = this.virtualScrollValue && this.hasUrlValue;

    if (useVirtualScroll) {
      plugins["virtual_scroll"] = {}
    }

    if (isMultiple) {
      plugins["remove_button"] = {
        label: controller.removeIconValue
      }

      plugins["checkbox_options"] = {}
      plugins["no_active_items"] = {}
    }

    if (!isDisabled && this.hasClearIconValue) {
      plugins["clear_button"] = {
        title: controller.clearTitleValue,
        html: function (data) {
          return controller.clearButtonTemplateValue.replace('%{TITLE}', data.title).replace('%{ICON}', controller.clearIconValue).replace('%{CLASS_NAME}', data.className)
        }
      }
    }

    if (this.optgroupColumnsValue) {
      plugins["optgroup_columns"] = {};
    }

    return plugins;
  }

  getRenderConfig() {
    return {
      option: (data, escape) => this.renderOption(data, escape),
      item: (data, escape) => this.renderSelectedItem(data, escape),
      no_results: (data, escape) => this.noResultsTemplateValue,
      option_create: (data, escape) => this.optionCreateTemplateValue.replace('%{TEXT}', escape(data.input)),
      loading_more: () => this.loadingMoreTemplateValue,
      no_more_results: () => this.noMoreResultsTemplateValue
    };
  }

  getVirtualScrollConfig() {
    return {
      valueField: this.valueFieldValue,
      labelField: this.labelFieldValue,
      searchField: this.labelFieldValue,
      firstUrl: (query) => this.buildApiUrl(this.urlValue, query, 1),
      load: this.virtualScrollLoad.bind(this),
      shouldLoadMore: this.shouldLoadMore.bind(this),
    };
  }

  getCustomScrollConfig() {
    return {
      valueField: this.valueFieldValue,
      labelField: this.labelFieldValue,
      searchField: this.labelFieldValue,
      load: this.customScrollLoad.bind(this),
    };
  }

  async virtualScrollLoad(query, callback) {
    const url = this.select.getUrl(query);
    const scrollState = this.captureScrollState(url);

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(response.statusText);

      const rawJson = await response.json();
      const json = this.transformApiResponse(rawJson);

      this.updateVirtualScrollState(url, query, json);
      callback(json.data);

      // For pages after the first, just maintain scroll position
      if (scrollState.currentPage > 1) {
        requestAnimationFrame(() => {
          if (this.select?.dropdown_content && typeof scrollState.scrollTop === "number") {
            this.select.dropdown_content.scrollTop = scrollState.scrollTop;
          }
        });
      } else {
        requestAnimationFrame(() => {
          this.restoreScrollState(scrollState);
          this.handlePostLoadFocus(query, scrollState);
        });
      }
    } catch (error) {
      console.error("Virtual scroll load error:", error);
      this.select?.setNextUrl(query, null);
      callback();
    } finally {
      this.cleanupScrollState();
    }
  }

  async customScrollLoad(query, callback) {
    this.resetPagination();

    try {
      const response = await this.fetchPage(query, 1);
      const json = this.transformApiResponse(response);

      callback(json.data);
      this.hasMore = json.has_more;

      if (this.select?.dropdown_content) {
        this.setupInfiniteScroll();
        setTimeout(() => this.focusFirstOption(query), 10);
      }
    } catch (error) {
      console.error("Custom scroll load error:", error);
      callback();
      this.hasMore = false;
    }
  }

  setupEventHandlers() {
    // Override setActiveOption for single active state
    const original = this.select.setActiveOption.bind(this.select);
    this.select.setActiveOption = (option, scroll) => {
      this.clearAllActiveStates();
      return original(option, scroll);
    };

    // Clear options if URL-based
    if (this.hasUrlValue) this.select.clearOptions();

    // Dropdown open handler
    this.select.on("dropdown_open", () => this.handleDropdownOpen());

    // Setup additional features
    if (this.element.multiple) this.setupScrollTracking();
    if (this.disableTypingValue) this.setupReadonlyInput();
  }

  setupPositioning() {
    this.scrollHandler = () => this.updatePosition();
    window.addEventListener("scroll", this.scrollHandler, true);

    this.resizeObserver = new ResizeObserver(() => this.updatePosition());
    this.resizeObserver.observe(document.documentElement);

    this.mutationObserver = new MutationObserver(() => {
      if (this.select?.dropdown?.classList.contains("ts-dropdown")) {
        this.updatePosition();
      }
    });
    this.mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  handleDropdownOpen() {
    this.clearAllActiveStates();
    this.select.setActiveOption(null);

    // Update position multiple times to ensure proper placement
    [0, 10, 50, 100].forEach((delay) => {
      setTimeout(() => this.updatePosition(), delay);
    });

    if (this.hasUrlValue && !this.virtualScrollValue) {
      this.setupInfiniteScroll();
      this.resetPagination();
    }
  }

  setupInfiniteScroll() {
    const content = this.select.dropdown_content;
    if (!content) return;

    const handler = this.handleScroll.bind(this);
    content.removeEventListener("scroll", handler);
    content.addEventListener("scroll", handler);
  }

  handleScroll() {
    if (this.virtualScrollValue || !this.select?.dropdown_content) return;

    const { scrollTop, scrollHeight, clientHeight } = this.select.dropdown_content;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      const query = this.select.control_input?.value || "";
      this.loadMore(query);
    }
  }

  async loadMore(query) {
    if (this.virtualScrollValue || this.loadingMore || !this.hasMore) return;

    this.loadingMore = true;
    this.currentPage += 1;

    const lastActiveValue = this.getActiveValue();

    try {
      const response = await this.fetchPage(query, this.currentPage);
      const newOptions = this.transformApiResponse(response);

      if (newOptions?.data?.length > 0) {
        this.select.addOptions(newOptions.data);
        this.hasMore = newOptions.has_more;

        setTimeout(() => this.restoreSelectionAfterLoading(lastActiveValue), 300);
      } else {
        this.hasMore = false;
      }

      this.select.control_input?.focus();
    } catch (error) {
      console.error("Load more error:", error);
      this.hasMore = false;
    } finally {
      this.loadingMore = false;
      this.updatePosition();
    }
  }

  setupScrollTracking() {
    const content = this.select.dropdown_content;
    if (!content) return;

    content.addEventListener("scroll", () => {
      this.lastScrollPosition = content.scrollTop;
    });

    ["item_add", "item_remove"].forEach((event) => {
      this.select.on(event, () => {
        if (this.lastScrollPosition) {
          setTimeout(() => {
            content.scrollTop = this.lastScrollPosition;
          }, 0);
        }
      });
    });
  }

  setupReadonlyInput() {
    const input = this.select.control_input;
    if (!input) return;

    input.readOnly = true;
    input.setAttribute("readonly", "readonly");

    let buffer = "";
    let timeout;

    input.addEventListener("keydown", (e) => {
      if (!this.isNavigationKey(e.key)) return;

      if (e.key.length === 1) {
        document.body.requestPointerLock();
        this.handleTypeAhead(e.key, buffer, timeout);
      }
    });

    document.addEventListener("mousemove", () => {
      if (document.pointerLockElement) document.exitPointerLock();
    });
  }

  handleTypeAhead(key, buffer, timeout) {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      buffer = "";
    }, 1000);

    buffer += key.toLowerCase();
    const match = this.findMatchingOption(buffer);

    if (match) {
      const optionEl = this.select.dropdown_content.querySelector(`[data-value="${match[this.valueFieldValue]}"]`);

      if (optionEl) {
        this.select.setActiveOption(optionEl);
        this.select.open();
        this.scrollToOption(optionEl);
      }
    }
  }

  async updatePosition() {
    if (!this.select?.dropdown) return;

    // Don't reposition during infinite scroll loading
    if (this.select.dropdown_content?.classList.contains("is-loading-more")) return;

    const reference = this.select.control;
    const floating = this.select.dropdown;

    if (!reference.getBoundingClientRect().height || !floating.getBoundingClientRect().height) {
      if (floating.offsetParent !== null) {
        setTimeout(() => this.updatePosition(), 50);
      }
      return;
    }

    try {
      const { x, y } = await computePosition(reference, floating, {
        placement: "bottom-start",
        middleware: [
          offset(({ placement }) => placement.startsWith("top") ? 6 : 0),
          flip(),
          shift({ padding: 8 })
        ],
      });

      Object.assign(floating.style, {
        position: "absolute",
        left: `${x}px`,
        top: `${y}px`,
        width: `${Math.max(reference.offsetWidth, 160)}px`,
      });
    } catch (error) {
      console.warn("Position update error:", error);
    }
  }

  handleChange(value) {
    if (value === "none") {
      this.element.value = ""
    } else {
      if (this.updateFieldValue) {
        this.updateTargetField(value);
      }
    }
  }

  updateTargetField(value) {
    let targetField = null

    if (this.updateFieldTargetValue) {
      targetField = this.element.closest("form").querySelector(this.updateFieldTargetValue)
    }

    if (!targetField) return;

    const selectedOption = this.select.options[value];
    if (!selectedOption) return;

    const data = this.parseOptionData(selectedOption);
    if (data?.[this.updateFieldSourceValue]) {
      targetField.value = data[this.updateFieldSourceValue];
      targetField.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  parseOptionData(option) {
    if (typeof option.text === "string" && option.text.startsWith("{")) {
      try {
        return JSON.parse(option.text);
      } catch (e) {
        console.warn("Parse error:", e);
      }
    }
    return null;
  }

  transformApiResponse(response) {
    const data = this.extractDataArray(response);
    const transformedData = (data || []).map((item) => ({
      ...item,
      text: item.text || item[this.labelFieldValue] || item.name || "",
      value: item.value || item[this.valueFieldValue],
    }));

    if (!this.virtualScrollValue) {
      const hasMore = this.detectHasMore(response, data);
      return { data: transformedData, has_more: hasMore };
    }

    return { data: transformedData };
  }

  extractDataArray(response) {
    if (this.responseDataFieldValue !== "data") {
      return this.getNestedValue(response, this.responseDataFieldValue);
    }

    if (Array.isArray(response)) return response;

    const fields = ["data", "results", "items"];
    for (const field of fields) {
      if (response[field] && Array.isArray(response[field])) {
        return response[field];
      }
    }

    return null;
  }

  detectHasMore(response, data) {
    return (
      response.has_more ||
      response.hasMore ||
      !!response.next ||
      !!response.next_page_url ||
      (response.info && !!response.info.next) ||
      (data && data.length === this.perPageValue) ||
      false
    );
  }

  buildApiUrl(baseUrl, query, page) {
    const url = new URL(baseUrl, window.location.origin);

    if (query) url.searchParams.set(this.searchParamValue, query);
    url.searchParams.set("page", page);

    const isExternalApi = !baseUrl.startsWith("/") && !baseUrl.startsWith(window.location.origin);
    if (!isExternalApi) {
      url.searchParams.set("per_page", this.perPageValue);
    }

    return url.toString();
  }

  async fetchPage(query, page) {
    const url = this.buildApiUrl(this.urlValue, query, page);
    const response = await fetch(url);
    if (!response.ok) throw new Error(response.statusText);
    return response.json();
  }

  renderOption(data, escape) {
    let text = escape(data.text)

    if (data.resultLabel) {
      text = data.resultLabel
    }

    return this.renderOptionTemplateValue.replace('%{TEXT}', text)
  }

  renderSelectedItem(data, escape) {
    let text = escape(data.text)

    if (data.selectedLabel) {
      text = data.selectedLabel
    }

    return this.renderSelectedItemTemplateValue.replace('%{TEXT}', text)
  }

  isNavigationKey(key) {
    return (
      (key.length === 1 && key.match(/[a-z0-9]/i)) || ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(key)
    );
  }

  getNestedValue(obj, path) {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  }

  clearAllActiveStates() {
    if (!this.select?.dropdown_content) return;

    this.select.dropdown_content.querySelectorAll(".option.active, .create.active").forEach((opt) => {
      opt.classList.remove("active");
      opt.setAttribute("aria-selected", "false");
    });

    if (this.select.activeOption) {
      this.select.activeOption = null;
    }
  }

  captureScrollState(url) {
    const currentUrl = new URL(url, window.location.origin);
    const currentPage = parseInt(currentUrl.searchParams.get("page") || "1");
    let state = { currentPage };

    if (this.select?.dropdown_content) {
      state.scrollTop = this.select.dropdown_content.scrollTop;
      state.scrollHandler = this.select.dropdown_content.onscroll;

      if (currentPage > 1) {
        const activeItem = this.select.dropdown_content.querySelector(".option.active");
        if (activeItem) {
          state.lastActiveValue = activeItem.getAttribute("data-value");
        }
      }

      this.select.dropdown_content.onscroll = null;
      this.select.dropdown_content.classList.add("is-loading-more");
    }

    return state;
  }

  restoreScrollState(state) {
    if (!this.select?.dropdown_content || !state) return;

    if (typeof state.scrollTop === "number") {
      this.select.dropdown_content.scrollTop = state.scrollTop;
    }

    this.select.dropdown_content.onscroll = state.scrollHandler;
  }

  cleanupScrollState() {
    if (this.select?.dropdown_content) {
      this.select.dropdown_content.classList.remove("is-loading-more");
    }
  }

  updateVirtualScrollState(url, query, json) {
    const currentUrl = new URL(url, window.location.origin);
    const currentPage = parseInt(currentUrl.searchParams.get("page") || "1");

    const hasMore = json.data.length === this.perPageValue || json.info?.next || json.next || json.has_more;

    if (hasMore) {
      const nextUrl = this.buildApiUrl(this.urlValue, query, currentPage + 1);
      this.select.setNextUrl(query, nextUrl);
    } else {
      this.select.setNextUrl(query, null);
    }
  }

  handlePostLoadFocus(query, scrollState) {
    if (!this.select?.dropdown_content) return;

    // Don't mess with focus/selection during infinite scroll
    if (scrollState.currentPage > 1) {
      // Just maintain the current scroll position
      return;
    }

    this.clearAllActiveStates();
    this.select.setActiveOption(null);

    if (scrollState.currentPage === 1) {
      this.focusFirstOption(query);
    }
  }

  focusFirstOption(query) {
    if (!query?.trim() || !this.select?.dropdown_content) return;

    const currentActive = this.select.dropdown_content.querySelector(".option.active");
    if (currentActive || this.select.activeOption) return;

    const firstOption = this.select.dropdown_content.querySelector(".option:not(.create):not(.no-results)");
    if (firstOption) {
      this.select.setActiveOption(firstOption);
    }
  }

  restoreSelectionAfterLoading(lastActiveValue) {
    if (!this.select?.dropdown_content || !lastActiveValue) return;

    const currentActive = this.select.dropdown_content.querySelector(".option.active");
    if (currentActive) return;

    const itemToRestore = this.select.dropdown_content.querySelector(`[data-value="${lastActiveValue}"]`);
    if (itemToRestore) {
      this.select.setActiveOption(itemToRestore);
    }
  }

  getActiveValue() {
    const activeItem = this.select?.dropdown_content?.querySelector(".option.active");
    return activeItem?.getAttribute("data-value");
  }

  findMatchingOption(buffer) {
    return Object.values(this.select.options).find((option) => {
      const label = this.hasUrlValue
        ? option[this.labelFieldValue]
        : this.parseOptionData(option)?.name || option.text;
      return label.toLowerCase().startsWith(buffer);
    });
  }

  scrollToOption(optionEl) {
    const content = this.select.dropdown_content;
    const dropdownHeight = content.offsetHeight;
    const optionTop = optionEl.offsetTop;
    const optionHeight = optionEl.offsetHeight;

    if (optionTop < content.scrollTop) {
      content.scrollTop = optionTop;
    } else if (optionTop + optionHeight > content.scrollTop + dropdownHeight) {
      content.scrollTop = optionTop + optionHeight - dropdownHeight;
    }
  }

  resetPagination() {
    this.currentPage = 1;
    this.hasMore = true;
    this.loadingMore = false;
  }

  shouldLoadMore() {
    if (!this.select?.dropdown_content) return false;
    const { scrollTop, scrollHeight, clientHeight } = this.select.dropdown_content;
    return scrollTop + clientHeight + 150 >= scrollHeight;
  }

  handleInitialValue() {
    if (!this.updateFieldValue || !this.hasUrlValue) return;

    try {
      const currentValue = this.getValue(this.urlValue);
      if (currentValue) {
        this.select.setValue(currentValue);
      }
    } catch (error) {
      console.warn("Initial value setting skipped");
    }

    // Re-init with dropdown_input if needed
    if (!this.disableDropdownInputValue && this.select.getValue()) {
      const options = this.buildOptions();
      options.plugins.push("dropdown_input");

      this.select.destroy();
      this.select = new TomSelect(this.element, options);
      this.setupEventHandlers();
    }
  }

  cleanup() {
    if (this.checkboxObserver) this.checkboxObserver.disconnect();
    if (this.select) {
      this.select.destroy();
      this.select = null;
    }

    window.removeEventListener("scroll", this.scrollHandler, true);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.mutationObserver) this.mutationObserver.disconnect();
  }
}
